import { inflateSync, unzlibSync } from "fflate";
import { checkSeriesTargetExists, fetchSeriesTargetEx } from "@api/files";
import type { AnalysisType, VOI } from "config/analysisConfig";
import {
  createServerFrontDataCprBaseImagePath,
  makeServerFrontDataLabelOverlayLayers,
  type CprAngleFrame,
  type CprCenterlinePathPoint,
  type CprCrossSectionImages,
  type CrossSectionFrameRef,
  type CprMode,
  type CprOverlayGeometry,
  type ImageLayerLabelValues,
} from "./cprImageResources";

type FrontDataModeName = "stretched" | "straightened" | string;

type FrontDataMode = {
  mode?: FrontDataModeName;
  zipPath?: string;
  zipFileName?: string;
  manifestPathInZip?: string;
  huDirectoryInZip?: string;
  labelDirectoryInZip?: string;
  mappingDirectoryInZip?: string;
  outlineDirectoryInZip?: string;
  frameCount?: number;
  seriesId?: string;
  angles?: number[];
};

type FrontDataBranch = {
  branchIndex?: number;
  branchId?: number;
  branchName?: string;
  branchNames?: string[];
  centerlinePointCount?: number;
  packedMaskPointCount?: number;
  modes?: Record<FrontDataModeName, FrontDataMode>;
};

type FrontDataViewData = {
  viewerDataExport?: {
    rootPath?: string;
    branchCount?: number;
    modes?: FrontDataModeName[];
    branches?: FrontDataBranch[];
  };
  parameterBaseDirectory?: string;
};

type FrontDataFrame = {
  angle?: number;
  angleDeg?: number;
  width?: number;
  height?: number;
  mode?: string;
  huRawPath?: string;
  huPath?: string;
  basePath?: string;
  huRawCompression?: string;
  huRawScalarType?: string;
  huRawEndianness?: string;
  labelPath?: string;
  mappingPath?: string;
  outlinePath?: string;
};

type FrontDataFrameManifest = {
  version?: number;
  seriesId?: string;
  frameVectorsPath?: string;
  frames?: FrontDataFrame[];
};

type FrontDataMapping = {
  angle?: number;
  mode?: string;
  width?: number;
  height?: number;
  outputCenterX?: number;
  rowToCenterlineIndex?: Array<number | null>;
  rowToCenterlineX?: Array<number | null>;
  rowToDistanceMm?: number[];
  path?: Array<{
    x?: number;
    y?: number;
    centerlineIndex?: number;
    distanceFromStartMm?: number;
  }>;
};

type FrontDataVec3 = {
  x?: number;
  y?: number;
  z?: number;
};

type FrontDataFrameVectorSample = {
  centerlineIndex?: number;
  point?: FrontDataVec3;
  tangent?: FrontDataVec3;
  e0?: FrontDataVec3;
  e1?: FrontDataVec3;
  normal?: FrontDataVec3;
};

type FrontDataFrameVectors = {
  samples?: FrontDataFrameVectorSample[];
};

type FrontDataOutline = {
  kind?: string;
  mode?: string;
  coordinateScale?: number;
  lines?: Array<{
    angle?: number;
    type?: string;
    closed?: boolean;
    points?: Array<{ x?: number; y?: number }>;
  }>;
};

type ZipDirectoryEntry = {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
};

type LoadedFrontDataZip = {
  branchId: number;
  branchName?: string;
  mode: CprMode;
  zipPath: string;
  zipByteLength: number;
  entryCount: number;
  manifestPathInZip: string;
  manifest: FrontDataFrameManifest;
  frameVectors?: FrontDataFrameVectors;
  entries: ZipDirectoryEntry[];
  directoryCounts: Record<string, number>;
};

type LoadedFrontDataZipWithBytes = LoadedFrontDataZip & {
  zipBytes: Uint8Array;
};

type LoadedZipSummary = {
  branchId: number;
  branchName?: string;
  mode: string;
  zipPath: string;
  zipByteLength: number;
  entryCount: number;
  manifestPathInZip: string;
  manifestFrameCount?: number;
  firstFrame?: unknown;
  directoryCounts: Record<string, number>;
};

type FrontDataCprResource = {
  mode: CprMode;
  angleFrames: CprAngleFrame[];
};

type FrontDataBranchResourceLoadResult = {
  branchId: string;
  branch: CprCrossSectionImages["branches"][string];
};

const VIEW_DATA_TARGET = "front_data/view_data.json";
const DEFAULT_MAIN_BRANCH_ORDER = ["RCA", "LAD", "LCX"];
const SUPPORTED_ANALYSIS_TYPES = new Set<AnalysisType>([
  "plaque",
  "stenosis",
  "fai",
  "isr",
  "aorticValveCalcium",
]);
const FRONT_DATA_CPR_MODES: CprMode[] = ["stretched", "straightened"];
const textDecoder = new TextDecoder("utf-8");
const pendingDebugLoads = new Map<string, Promise<LoadedZipSummary[]>>();
const pendingImageManifests = new Map<string, Promise<CprCrossSectionImages>>();
const pendingBranchManifests = new Map<
  string,
  Promise<{
    branchId: string;
    branch: CprCrossSectionImages["branches"][string];
  }>
>();

type CurrentFrontDataSeriesCache = {
  seriesId: string;
  requestToken: number;
  bytesByTarget: Map<string, Uint8Array>;
  pendingBytesByTarget: Map<string, Promise<Uint8Array>>;
  loadedZipsByCacheKey: Map<string, LoadedFrontDataZipWithBytes>;
  pendingLoadedZipsByCacheKey: Map<string, Promise<LoadedFrontDataZipWithBytes>>;
  cprResourcesByCacheKey: Map<string, FrontDataCprResource>;
  branchResourcesByCacheKey: Map<string, FrontDataBranchResourceLoadResult>;
  imageManifestsByCacheKey: Map<string, CprCrossSectionImages>;
  viewData?: FrontDataViewData;
  viewDataPromise?: Promise<FrontDataViewData>;
  frontDataAvailable?: boolean;
  frontDataAvailablePromise?: Promise<boolean>;
  preloadStarted: boolean;
};

let currentFrontDataSeriesCache: CurrentFrontDataSeriesCache | null = null;
let currentFrontDataSeriesRequestToken = 0;

const FRONT_DATA_PRELOAD_CONCURRENCY = 3;
const FRONT_DATA_BRANCH_MANIFEST_PREFIX = "front-data://branch/";
const FRONT_DATA_DEBUG_LOG_ENABLED = false;
const FRONT_DATA_DEBUG_STORAGE_KEY = "solution1.frontDataDebug";

const DEFAULT_CORONARY_LABEL_LAYERS: ImageLayerLabelValues = {
  lumen: [255],
  lumenoutline: [255],
  plaque: [1, 2, 3],
  plaqueoutline: [1, 2, 3, 255],
  fai: [251, 252, 253, 254],
};

const DEFAULT_AORTA_LABEL_LAYERS: ImageLayerLabelValues = {
  aorta: [255],
};

const FRONT_DATA_STENOSIS_LABEL_LAYERS: ImageLayerLabelValues = {
  lumen: DEFAULT_CORONARY_LABEL_LAYERS.lumen,
  lumenoutline: DEFAULT_CORONARY_LABEL_LAYERS.lumenoutline,
};

const FRONT_DATA_FAI_LABEL_LAYERS: ImageLayerLabelValues = {
  ...FRONT_DATA_STENOSIS_LABEL_LAYERS,
  fai: DEFAULT_CORONARY_LABEL_LAYERS.fai,
};

function getFrontDataLabelLayersForAnalysisType(
  analysisType: AnalysisType,
): ImageLayerLabelValues {
  if (isAorticFrontDataAnalysisType(analysisType)) {
    return DEFAULT_AORTA_LABEL_LAYERS;
  }
  if (analysisType === 'fai') {
    return FRONT_DATA_FAI_LABEL_LAYERS;
  }
  if (analysisType === 'stenosis' || analysisType === 'isr') {
    return FRONT_DATA_STENOSIS_LABEL_LAYERS;
  }
  return DEFAULT_CORONARY_LABEL_LAYERS;
}

function shouldIncludeFrontDataPlaqueOutline(analysisType: AnalysisType) {
  return analysisType === 'plaque';
}

const DEFAULT_AORTA_BRANCH_IDS = [49, 48, 0];
const DEFAULT_AORTA_CPR_ZIP_PATHS = [
  "front_data/cpr_aorta_straightened.zip",
  "front_data/cpr_aorta.zip",
];
const DEFAULT_AORTA_CPR_PRELOAD_ZIP_PATHS = [
  "front_data/cpr_aorta_straightened.zip",
];

const FRONT_DATA_OUTLINE_FLIP_Y = false;

function flipYCoordinate(y: number, height: number) {
  return Math.max(0, height - 1) - y;
}

function finitePointOrNull(point: { x?: number; y?: number } | undefined) {
  if (!point) return null;
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function toSafeInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.floor(parsed) : undefined;
}

function toFiniteNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toOptionalNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isAorticFrontDataAnalysisType(
  analysisType: AnalysisType | string | null | undefined,
) {
  return analysisType === "aorticValveCalcium";
}

function decodeSvgAttributeValue(value: string) {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function svgAttribute(attributes: string, name: string) {
  const pattern = new RegExp(
    `${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`,
    "i",
  );
  const match = attributes.match(pattern);
  const raw = match?.[1] ?? match?.[2];
  return raw === undefined ? undefined : decodeSvgAttributeValue(raw);
}

function isSvgCommand(token: string) {
  return /^[MmLlHhVvZz]$/.test(token);
}

function parseSvgPathPoints(pathD: string) {
  const tokens = pathD.match(/[MmLlHhVvZz]|[-+]?(?:\d*\.\d+|\d+)(?:[eE][-+]?\d+)?/g) ?? [];
  const points: Array<{ x: number; y: number }> = [];
  let index = 0;
  let command = "";
  let currentX = 0;
  let currentY = 0;
  let closed = false;

  const readNumber = () => {
    if (index >= tokens.length || isSvgCommand(tokens[index])) return undefined;
    const value = Number(tokens[index]);
    index += 1;
    return Number.isFinite(value) ? value : undefined;
  };

  while (index < tokens.length) {
    const token = tokens[index];
    if (isSvgCommand(token)) {
      command = token;
      index += 1;
      if (command === "Z" || command === "z") {
        closed = true;
        continue;
      }
    }

    if (command === "M" || command === "m" || command === "L" || command === "l") {
      const x = readNumber();
      const y = readNumber();
      if (x === undefined || y === undefined) break;
      currentX = command === "m" || command === "l" ? currentX + x : x;
      currentY = command === "m" || command === "l" ? currentY + y : y;
      points.push({ x: currentX, y: currentY });
      if (command === "M") command = "L";
      if (command === "m") command = "l";
      continue;
    }

    if (command === "H" || command === "h") {
      const x = readNumber();
      if (x === undefined) break;
      currentX = command === "h" ? currentX + x : x;
      points.push({ x: currentX, y: currentY });
      continue;
    }

    if (command === "V" || command === "v") {
      const y = readNumber();
      if (y === undefined) break;
      currentY = command === "v" ? currentY + y : y;
      points.push({ x: currentX, y: currentY });
      continue;
    }

    index += 1;
  }

  return { points, closed };
}

function parseSvgOutline(svgText: string, fallbackAngle?: number): FrontDataOutline {
  const lines: NonNullable<FrontDataOutline["lines"]> = [];
  const pathPattern = /<path\b([^>]*)>/gi;
  let match: RegExpExecArray | null;

  while ((match = pathPattern.exec(svgText)) !== null) {
    const attributes = match[1] ?? "";
    const pathD = svgAttribute(attributes, "d");
    if (!pathD) continue;
    const parsed = parseSvgPathPoints(pathD);
    if (parsed.points.length < 2) continue;
    const angle = toOptionalNumber(svgAttribute(attributes, "data-angle")) ?? fallbackAngle;
    const type =
      svgAttribute(attributes, "data-type") ??
      svgAttribute(attributes, "data-layer") ??
      svgAttribute(attributes, "id") ??
      "outline";
    lines.push({
      angle,
      type,
      closed: parsed.closed || /(^|\s)[Zz](\s|$)/.test(pathD),
      points: parsed.points,
    });
  }

  const svgTag = svgText.match(/<svg\b([^>]*)>/i)?.[1] ?? "";
  const coordinateScale = toOptionalNumber(
    svgAttribute(svgTag, "data-coordinate-scale") ??
      svgAttribute(svgTag, "coordinateScale"),
  );

  return {
    kind: "outline",
    coordinateScale,
    lines,
  };
}

function toVec3Tuple(value: FrontDataVec3 | undefined): [number, number, number] | undefined {
  const x = toOptionalNumber(value?.x);
  const y = toOptionalNumber(value?.y);
  const z = toOptionalNumber(value?.z);
  if (x === undefined || y === undefined || z === undefined) return undefined;
  return [x, y, z];
}

function normalizeTuple3(
  value: [number, number, number] | undefined,
): [number, number, number] | undefined {
  if (!value) return undefined;
  const length = Math.hypot(value[0], value[1], value[2]);
  if (!Number.isFinite(length) || length < 1e-8) return undefined;
  return [value[0] / length, value[1] / length, value[2] / length];
}

function toPositiveInt(value: unknown, fallback = 1) {
  const parsed = Math.floor(toFiniteNumber(value, fallback));
  return parsed > 0 ? parsed : fallback;
}

function normalizeAssetPath(path: string) {
  return path.replace(/^\.\//, "").replace(/^\/+/, "");
}

function stableStringify(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(",")}}`;
}

function isFrontDataDebugLogEnabled() {
  if (FRONT_DATA_DEBUG_LOG_ENABLED) return true;
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem(FRONT_DATA_DEBUG_STORAGE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

function logFrontDataDebug(message: string, payload?: Record<string, unknown>) {
  if (!isFrontDataDebugLogEnabled()) return;
  console.debug(message, payload);
}

function stripFrontDataPrefix(path: string) {
  const normalized = normalizeAssetPath(path);
  return normalized.startsWith("front_data/")
    ? normalized.slice("front_data/".length)
    : normalized;
}

function toUint8Array(data: unknown): Uint8Array {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    const view = data as ArrayBufferView;
    return new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  }
  throw new Error("unsupported binary response");
}

export function clearServerCprFrontDataRuntimeCache(keepSeriesId?: string) {
  if (
    keepSeriesId &&
    currentFrontDataSeriesCache?.seriesId === keepSeriesId
  ) {
    return;
  }

  currentFrontDataSeriesRequestToken += 1;
  currentFrontDataSeriesCache = null;
  pendingDebugLoads.clear();
  pendingImageManifests.clear();
  pendingBranchManifests.clear();
}

export function isServerCprFrontDataRuntimeCacheCurrent(seriesId: string) {
  return currentFrontDataSeriesCache?.seriesId === seriesId;
}

function getCurrentFrontDataSeriesCache(seriesId: string) {
  if (currentFrontDataSeriesCache?.seriesId !== seriesId) {
    clearServerCprFrontDataRuntimeCache();
    currentFrontDataSeriesCache = {
      seriesId,
      requestToken: currentFrontDataSeriesRequestToken,
      bytesByTarget: new Map(),
      pendingBytesByTarget: new Map(),
      loadedZipsByCacheKey: new Map(),
      pendingLoadedZipsByCacheKey: new Map(),
      cprResourcesByCacheKey: new Map(),
      branchResourcesByCacheKey: new Map(),
      imageManifestsByCacheKey: new Map(),
      preloadStarted: false,
    };
  }
  return currentFrontDataSeriesCache;
}

async function fetchSeriesTargetBytesRaw({
  accessToken,
  seriesId,
  target,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
}) {
  const normalizedTarget = normalizeAssetPath(target);
  logFrontDataDebug("[front_data][api] fetch target", {
    seriesId,
    target: normalizedTarget,
  });

  const response = await fetchSeriesTargetEx({
    accessToken,
    seriesId,
    target: normalizedTarget,
  });

  if (!response || response.status !== 200 || !response.data) {
    throw new Error(`server file download failed: ${normalizedTarget}`);
  }

  return toUint8Array(response.data);
}

async function fetchSeriesTargetBytes({
  accessToken,
  seriesId,
  target,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
}) {
  const normalizedTarget = normalizeAssetPath(target);
  if (!normalizedTarget.startsWith("front_data/")) {
    return fetchSeriesTargetBytesRaw({ accessToken, seriesId, target: normalizedTarget });
  }

  const cache = getCurrentFrontDataSeriesCache(seriesId);
  const cachedBytes = cache.bytesByTarget.get(normalizedTarget);
  if (cachedBytes) {
    logFrontDataDebug("[front_data][cache] bytes hit", {
      seriesId,
      target: normalizedTarget,
      bytes: cachedBytes.byteLength,
    });
    return cachedBytes;
  }

  const pendingBytes = cache.pendingBytesByTarget.get(normalizedTarget);
  if (pendingBytes) {
    logFrontDataDebug("[front_data][cache] bytes pending", {
      seriesId,
      target: normalizedTarget,
    });
    return pendingBytes;
  }

  logFrontDataDebug("[front_data][cache] bytes miss", {
    seriesId,
    target: normalizedTarget,
  });

  const promise = fetchSeriesTargetBytesRaw({
    accessToken,
    seriesId,
    target: normalizedTarget,
  })
    .then((bytes) => {
      if (
        currentFrontDataSeriesCache === cache &&
        currentFrontDataSeriesCache.seriesId === seriesId &&
        currentFrontDataSeriesCache.requestToken === cache.requestToken
      ) {
        cache.bytesByTarget.set(normalizedTarget, bytes);
      }
      return bytes;
    })
    .finally(() => {
      if (
        currentFrontDataSeriesCache === cache &&
        currentFrontDataSeriesCache.requestToken === cache.requestToken
      ) {
        cache.pendingBytesByTarget.delete(normalizedTarget);
      }
    });

  cache.pendingBytesByTarget.set(normalizedTarget, promise);
  return promise;
}

export async function fetchServerCprFrontDataTargetBytesCached({
  accessToken,
  seriesId,
  target,
}: {
  accessToken: string;
  seriesId: string;
  target: string;
}) {
  const normalizedTarget = normalizeAssetPath(target);
  if (!normalizedTarget.startsWith("front_data/")) {
    throw new Error(`not a front_data target: ${normalizedTarget}`);
  }
  return fetchSeriesTargetBytes({ accessToken, seriesId, target: normalizedTarget });
}

function collectFrontDataZipPaths(viewData: FrontDataViewData) {
  const paths = new Set<string>();
  (viewData.viewerDataExport?.branches ?? []).forEach((branch) => {
    selectModes(viewData, branch).forEach((mode) => {
      const zipPath = mode.zipPath ?? mode.zipFileName;
      if (!zipPath) return;
      paths.add(normalizeAssetPath(
        mode.zipPath ?? `front_data/${mode.zipFileName}`,
      ));
    });
  });
  // Preload only the canonical AVC aorta zip.  The legacy fallback
  // cpr_aorta.zip is still tried by loadAortaFrontDataZip() if the
  // canonical file is missing, but preloading it on every series creates
  // noisy 404 requests on current front_data exports.
  DEFAULT_AORTA_CPR_PRELOAD_ZIP_PATHS.forEach((candidate) => {
    paths.add(normalizeAssetPath(candidate));
  });
  return Array.from(paths);
}

function startFrontDataZipPreload({
  accessToken,
  seriesId,
  viewData,
}: {
  accessToken: string;
  seriesId: string;
  viewData: FrontDataViewData;
}) {
  const cache = getCurrentFrontDataSeriesCache(seriesId);
  if (cache.preloadStarted) return;

  const zipPaths = collectFrontDataZipPaths(viewData);
  if (zipPaths.length === 0) return;

  cache.preloadStarted = true;
  let nextIndex = 0;
  const worker = async () => {
    while (
      currentFrontDataSeriesCache === cache &&
      currentFrontDataSeriesCache.requestToken === cache.requestToken &&
      nextIndex < zipPaths.length
    ) {
      const target = zipPaths[nextIndex++];
      if (cache.bytesByTarget.has(target)) continue;
      try {
        await fetchSeriesTargetBytes({ accessToken, seriesId, target });
      } catch (err) {
        // Aorta fallback candidates may not exist on every series. Keep preload best-effort.
        if (!DEFAULT_AORTA_CPR_ZIP_PATHS.includes(target)) {
          console.warn("[front_data] preload failed", { seriesId, target, err });
        }
      }
    }
  };

  const workerCount = Math.min(FRONT_DATA_PRELOAD_CONCURRENCY, zipPaths.length);
  void Promise.all(Array.from({ length: workerCount }, worker));
}

export async function checkServerCprFrontDataAvailableOnce({
  accessToken,
  seriesId,
}: {
  accessToken: string;
  seriesId: string;
}) {
  const cache = getCurrentFrontDataSeriesCache(seriesId);
  if (cache.viewData) {
    cache.frontDataAvailable = true;
    return true;
  }
  if (typeof cache.frontDataAvailable === "boolean") {
    return cache.frontDataAvailable;
  }
  if (cache.frontDataAvailablePromise) {
    return cache.frontDataAvailablePromise;
  }

  const promise = (async () => {
    if (cache.viewDataPromise) {
      try {
        await cache.viewDataPromise;
        return true;
      } catch {
        return false;
      }
    }

    return checkSeriesTargetExists({
      accessToken,
      seriesId,
      target: VIEW_DATA_TARGET,
    });
  })().finally(() => {
    if (
      currentFrontDataSeriesCache === cache &&
      currentFrontDataSeriesCache.requestToken === cache.requestToken
    ) {
      cache.frontDataAvailablePromise = undefined;
    }
  });

  cache.frontDataAvailablePromise = promise;
  const available = await promise;
  if (
    currentFrontDataSeriesCache === cache &&
    currentFrontDataSeriesCache.requestToken === cache.requestToken
  ) {
    cache.frontDataAvailable = available;
  }
  return available;
}

async function loadViewData({
  accessToken,
  seriesId,
}: {
  accessToken: string;
  seriesId: string;
}): Promise<FrontDataViewData> {
  const cache = getCurrentFrontDataSeriesCache(seriesId);
  if (cache.viewData) {
    logFrontDataDebug("[front_data][cache] view_data hit", { seriesId });
    return cache.viewData;
  }
  if (cache.viewDataPromise) {
    logFrontDataDebug("[front_data][cache] view_data pending", { seriesId });
    return cache.viewDataPromise;
  }

  logFrontDataDebug("[front_data][cache] view_data miss", { seriesId });

  const promise = (async () => {
    const raw = textDecoder.decode(
      await fetchSeriesTargetBytes({
        accessToken,
        seriesId,
        target: VIEW_DATA_TARGET,
      }),
    );
    const parsed = JSON.parse(raw) as unknown;
    if (!isObject(parsed) || !isObject(parsed.viewerDataExport)) {
      throw new Error("front_data/view_data.json has invalid format");
    }
    const viewData = parsed as FrontDataViewData;
    if (
      currentFrontDataSeriesCache === cache &&
      currentFrontDataSeriesCache.requestToken === cache.requestToken
    ) {
      cache.viewData = viewData;
      cache.frontDataAvailable = true;
      startFrontDataZipPreload({ accessToken, seriesId, viewData });
    }
    return viewData;
  })().finally(() => {
    if (
      currentFrontDataSeriesCache === cache &&
      currentFrontDataSeriesCache.requestToken === cache.requestToken
    ) {
      cache.viewDataPromise = undefined;
    }
  });

  cache.viewDataPromise = promise;
  return promise;
}

function isMainBranchCandidate(branch: FrontDataBranch, name: string) {
  const expected = name.trim().toLowerCase();
  const branchName = branch.branchName?.trim().toLowerCase();
  if (branchName === expected) return true;
  return (branch.branchNames ?? []).some(
    (candidate) => candidate.trim().toLowerCase() === expected,
  );
}

function resolveFallbackMainBranchIds(viewData: FrontDataViewData) {
  const branches = viewData.viewerDataExport?.branches ?? [];
  return DEFAULT_MAIN_BRANCH_ORDER.map((name) =>
    branches.find((branch) => isMainBranchCandidate(branch, name)),
  )
    .map((branch) => toSafeInt(branch?.branchId ?? branch?.branchIndex))
    .filter((branchId): branchId is number => branchId !== undefined);
}

function normalizeMainBranchIds(
  mainBranchIds: readonly number[] | undefined,
  viewData: FrontDataViewData,
) {
  const explicit = (mainBranchIds ?? [])
    .map((branchId) => toSafeInt(branchId))
    .filter((branchId): branchId is number => branchId !== undefined);
  const uniqueExplicit = Array.from(new Set(explicit));
  if (uniqueExplicit.length >= 3) return uniqueExplicit.slice(0, 3);
  return resolveFallbackMainBranchIds(viewData).slice(0, 3);
}

function selectBranchIdsForAnalysis({
  analysisType,
  mainBranchIds,
  viewData,
}: {
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
  viewData: FrontDataViewData;
}) {
  if (analysisType === "fai") {
    const ids = normalizeMainBranchIds(mainBranchIds, viewData);
    return ids.length ? ids : [0];
  }
  return [0];
}

function findBranch(viewData: FrontDataViewData, branchId: number) {
  return (viewData.viewerDataExport?.branches ?? []).find((branch) => {
    const id = toSafeInt(branch.branchId ?? branch.branchIndex);
    return id === branchId;
  });
}

function selectModes(viewData: FrontDataViewData, branch: FrontDataBranch) {
  const modeOrder = viewData.viewerDataExport?.modes?.length
    ? viewData.viewerDataExport.modes
    : Object.keys(branch.modes ?? {});
  return modeOrder
    .map((modeName) => branch.modes?.[modeName])
    .filter(
      (mode): mode is FrontDataMode => !!mode?.zipFileName || !!mode?.zipPath,
    )
    .filter((mode) => FRONT_DATA_CPR_MODES.includes(mode.mode as CprMode));
}

function readUint16(view: DataView, offset: number) {
  return view.getUint16(offset, true);
}

function readUint32(view: DataView, offset: number) {
  return view.getUint32(offset, true);
}

function findEndOfCentralDirectory(view: DataView) {
  const minOffset = Math.max(0, view.byteLength - 0xffff - 22);
  for (let offset = view.byteLength - 22; offset >= minOffset; offset -= 1) {
    if (readUint32(view, offset) === 0x06054b50) return offset;
  }
  throw new Error("zip EOCD not found");
}

function listZipEntries(zipBytes: Uint8Array): ZipDirectoryEntry[] {
  const view = new DataView(
    zipBytes.buffer,
    zipBytes.byteOffset,
    zipBytes.byteLength,
  );
  const eocdOffset = findEndOfCentralDirectory(view);
  const entryCount = readUint16(view, eocdOffset + 10);
  const centralDirectoryOffset = readUint32(view, eocdOffset + 16);
  const entries: ZipDirectoryEntry[] = [];
  let offset = centralDirectoryOffset;

  for (let i = 0; i < entryCount; i += 1) {
    if (readUint32(view, offset) !== 0x02014b50) {
      throw new Error(`zip central directory is invalid at ${offset}`);
    }
    const compressionMethod = readUint16(view, offset + 10);
    const compressedSize = readUint32(view, offset + 20);
    const uncompressedSize = readUint32(view, offset + 24);
    const nameLength = readUint16(view, offset + 28);
    const extraLength = readUint16(view, offset + 30);
    const commentLength = readUint16(view, offset + 32);
    const localHeaderOffset = readUint32(view, offset + 42);
    const nameBytes = zipBytes.subarray(offset + 46, offset + 46 + nameLength);
    const name = textDecoder.decode(nameBytes);

    entries.push({
      name,
      compressionMethod,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

function extractZipEntry(zipBytes: Uint8Array, entry: ZipDirectoryEntry) {
  const view = new DataView(
    zipBytes.buffer,
    zipBytes.byteOffset,
    zipBytes.byteLength,
  );
  const offset = entry.localHeaderOffset;
  if (readUint32(view, offset) !== 0x04034b50) {
    throw new Error(`zip local header is invalid: ${entry.name}`);
  }
  const nameLength = readUint16(view, offset + 26);
  const extraLength = readUint16(view, offset + 28);
  const dataOffset = offset + 30 + nameLength + extraLength;
  const compressed = zipBytes.subarray(
    dataOffset,
    dataOffset + entry.compressedSize,
  );

  if (entry.compressionMethod === 0) return compressed;
  if (entry.compressionMethod === 8) return inflateSync(compressed);
  throw new Error(
    `unsupported zip compression method ${entry.compressionMethod}: ${entry.name}`,
  );
}

function countEntriesByDirectory(
  entries: ZipDirectoryEntry[],
  directory?: string,
) {
  if (!directory) return 0;
  const prefix = `${directory.replace(/^\/+|\/+$/g, "")}/`;
  return entries.filter((entry) => entry.name.startsWith(prefix)).length;
}

function findZipEntry(entries: ZipDirectoryEntry[], path: string | undefined) {
  if (!path) return undefined;
  const cleanPath = normalizeAssetPath(stripFrontDataPrefix(path));
  return (
    entries.find((entry) => entry.name === cleanPath) ??
    entries.find((entry) => entry.name.endsWith(`/${cleanPath}`))
  );
}

function parseJsonZipEntry<T>(
  zipBytes: Uint8Array,
  entries: ZipDirectoryEntry[],
  path: string | undefined,
): T | undefined {
  const entry = findZipEntry(entries, path);
  if (!entry) return undefined;
  return JSON.parse(textDecoder.decode(extractZipEntry(zipBytes, entry))) as T;
}

function parseTextZipEntry(
  zipBytes: Uint8Array,
  entries: ZipDirectoryEntry[],
  path: string | undefined,
): string | undefined {
  const entry = findZipEntry(entries, path);
  if (!entry) return undefined;
  return textDecoder.decode(extractZipEntry(zipBytes, entry));
}

function parseOutlineZipEntry(
  zipBytes: Uint8Array,
  entries: ZipDirectoryEntry[],
  path: string | undefined,
  fallbackAngle?: number,
): FrontDataOutline | undefined {
  if (!path) return undefined;
  if (path.toLowerCase().endsWith(".svg")) {
    const svgText = parseTextZipEntry(zipBytes, entries, path);
    return svgText ? parseSvgOutline(svgText, fallbackAngle) : undefined;
  }
  return parseJsonZipEntry<FrontDataOutline>(zipBytes, entries, path);
}

function extractOptionalZipEntryBytes(
  zipBytes: Uint8Array,
  entries: ZipDirectoryEntry[],
  path: string | undefined,
): Uint8Array | undefined {
  const entry = findZipEntry(entries, path);
  return entry ? extractZipEntry(zipBytes, entry) : undefined;
}

function frontDataLoadedZipCacheKey({
  zipAssetPath,
  manifestPathInZip,
  mode,
}: {
  zipAssetPath: string;
  manifestPathInZip: string;
  mode?: string;
}) {
  return [normalizeAssetPath(zipAssetPath), manifestPathInZip, mode ?? "default"].join("|");
}

function cloneLoadedZipForBranch({
  loaded,
  branchId,
  branchName,
}: {
  loaded: LoadedFrontDataZipWithBytes;
  branchId: number;
  branchName?: string;
}): LoadedFrontDataZipWithBytes {
  if (loaded.branchId === branchId && loaded.branchName === branchName) return loaded;
  return {
    ...loaded,
    branchId,
    branchName,
  };
}

async function loadFrontDataZipWithBytesCached({
  accessToken,
  seriesId,
  branch,
  branchId,
  mode,
}: {
  accessToken: string;
  seriesId: string;
  branch: FrontDataBranch;
  branchId: number;
  mode: FrontDataMode;
}): Promise<LoadedFrontDataZipWithBytes> {
  const zipAssetPath = normalizeAssetPath(
    mode.zipPath ?? `front_data/${mode.zipFileName}`,
  );
  const manifestPathInZip =
    mode.manifestPathInZip ?? "image_extraction_manifest.json";
  const cacheKey = frontDataLoadedZipCacheKey({
    zipAssetPath,
    manifestPathInZip,
    mode: mode.mode,
  });
  const cache = getCurrentFrontDataSeriesCache(seriesId);
  const cached = cache.loadedZipsByCacheKey.get(cacheKey);
  if (cached) {
    logFrontDataDebug("[front_data][cache] parsed zip hit", {
      seriesId,
      branchId,
      zipAssetPath,
      cacheKey,
    });
    return cloneLoadedZipForBranch({
      loaded: cached,
      branchId,
      branchName: branch.branchName,
    });
  }

  const existing = cache.pendingLoadedZipsByCacheKey.get(cacheKey);
  if (existing) {
    logFrontDataDebug("[front_data][cache] parsed zip pending", {
      seriesId,
      branchId,
      zipAssetPath,
      cacheKey,
    });
    const loaded = await existing;
    return cloneLoadedZipForBranch({
      loaded,
      branchId,
      branchName: branch.branchName,
    });
  }

  logFrontDataDebug("[front_data][cache] parsed zip miss", {
    seriesId,
    branchId,
    zipAssetPath,
    cacheKey,
  });

  const promise = (async () => {
    const zipBytes = await fetchSeriesTargetBytes({
      accessToken,
      seriesId,
      target: zipAssetPath,
    });
    const entries = listZipEntries(zipBytes);
    const manifest = parseJsonZipEntry<FrontDataFrameManifest>(
      zipBytes,
      entries,
      manifestPathInZip,
    );
    if (!manifest || !Array.isArray(manifest.frames)) {
      throw new Error(`front_data CPR manifest is invalid: ${zipAssetPath}`);
    }
    const frameVectors = parseJsonZipEntry<FrontDataFrameVectors>(
      zipBytes,
      entries,
      manifest.frameVectorsPath ?? "frame_vectors.json",
    );
    const loaded: LoadedFrontDataZipWithBytes = {
      branchId,
      branchName: branch.branchName,
      mode: (mode.mode ?? manifest.frames[0]?.mode ?? "stretched") as CprMode,
      zipPath: zipAssetPath,
      zipByteLength: zipBytes.byteLength,
      entryCount: entries.length,
      manifestPathInZip,
      manifest,
      frameVectors,
      entries,
      directoryCounts: {
        hu: countEntriesByDirectory(entries, mode.huDirectoryInZip ?? "hu"),
        label: countEntriesByDirectory(
          entries,
          mode.labelDirectoryInZip ?? "label",
        ),
        mapping: countEntriesByDirectory(
          entries,
          mode.mappingDirectoryInZip ?? "mapping",
        ),
        outline: countEntriesByDirectory(
          entries,
          mode.outlineDirectoryInZip ?? "outline",
        ),
      },
      zipBytes,
    };
    logFrontDataDebug("[front_data] CPR zip parsed", {
      branchId: loaded.branchId,
      branchName: loaded.branchName,
      mode: loaded.mode,
      zipPath: loaded.zipPath,
      zipByteLength: loaded.zipByteLength,
      entryCount: loaded.entryCount,
      manifestPathInZip: loaded.manifestPathInZip,
      manifestFrameCount: loaded.manifest.frames?.length,
      directoryCounts: loaded.directoryCounts,
    });
    return loaded;
  })()
    .then((loaded) => {
      if (
        currentFrontDataSeriesCache === cache &&
        currentFrontDataSeriesCache.seriesId === seriesId &&
        currentFrontDataSeriesCache.requestToken === cache.requestToken
      ) {
        cache.loadedZipsByCacheKey.set(cacheKey, loaded);
      }
      return loaded;
    })
    .finally(() => {
      if (
        currentFrontDataSeriesCache === cache &&
        currentFrontDataSeriesCache.requestToken === cache.requestToken
      ) {
        cache.pendingLoadedZipsByCacheKey.delete(cacheKey);
      }
    });

  cache.pendingLoadedZipsByCacheKey.set(cacheKey, promise);
  const loaded = await promise;
  return cloneLoadedZipForBranch({
    loaded,
    branchId,
    branchName: branch.branchName,
  });
}

async function loadFrontDataZip({
  accessToken,
  seriesId,
  branch,
  branchId,
  mode,
}: {
  accessToken: string;
  seriesId: string;
  branch: FrontDataBranch;
  branchId: number;
  mode: FrontDataMode;
}): Promise<LoadedFrontDataZip> {
  const zipAssetPath = normalizeAssetPath(
    mode.zipPath ?? `front_data/${mode.zipFileName}`,
  );
  const zipBytes = await fetchSeriesTargetBytes({
    accessToken,
    seriesId,
    target: zipAssetPath,
  });
  const entries = listZipEntries(zipBytes);
  const manifestPathInZip =
    mode.manifestPathInZip ?? "image_extraction_manifest.json";
  const manifest = parseJsonZipEntry<FrontDataFrameManifest>(
    zipBytes,
    entries,
    manifestPathInZip,
  );
  if (!manifest || !Array.isArray(manifest.frames)) {
    throw new Error(`front_data CPR manifest is invalid: ${zipAssetPath}`);
  }
  const frameVectors = parseJsonZipEntry<FrontDataFrameVectors>(
    zipBytes,
    entries,
    manifest.frameVectorsPath ?? "frame_vectors.json",
  );

  return {
    branchId,
    branchName: branch.branchName,
    mode: (mode.mode ?? "stretched") as CprMode,
    zipPath: zipAssetPath,
    zipByteLength: zipBytes.byteLength,
    entryCount: entries.length,
    manifestPathInZip,
    manifest,
    frameVectors,
    entries,
    directoryCounts: {
      hu: countEntriesByDirectory(entries, mode.huDirectoryInZip ?? "hu"),
      label: countEntriesByDirectory(
        entries,
        mode.labelDirectoryInZip ?? "label",
      ),
      mapping: countEntriesByDirectory(
        entries,
        mode.mappingDirectoryInZip ?? "mapping",
      ),
      outline: countEntriesByDirectory(
        entries,
        mode.outlineDirectoryInZip ?? "outline",
      ),
    },
  };
}

function loadZipSummary(args: Parameters<typeof loadFrontDataZip>[0]) {
  return loadFrontDataZip(args).then((loaded) => ({
    branchId: loaded.branchId,
    branchName: loaded.branchName,
    mode: loaded.mode,
    zipPath: loaded.zipPath,
    zipByteLength: loaded.zipByteLength,
    entryCount: loaded.entryCount,
    manifestPathInZip: loaded.manifestPathInZip,
    manifestFrameCount: loaded.manifest.frames?.length,
    firstFrame: loaded.manifest.frames?.[0],
    directoryCounts: loaded.directoryCounts,
  }));
}

function dtypeBytes(dtype: string | undefined) {
  const clean = (dtype ?? "uint8").toLowerCase();
  if (clean.includes("int16") || clean.includes("uint16") || clean === "i16") {
    return 2;
  }
  if (clean.includes("float32") || clean === "f32") return 4;
  return 1;
}

function normalizeInnerCompression(compression: string | undefined) {
  const clean = compression?.trim().toLowerCase();
  if (!clean || clean === "none" || clean === "raw") return undefined;
  if (clean === "zlib-deflate" || clean === "zlib") return "zlib-deflate";
  return clean;
}

function decodeFrontDataInnerBytes(
  data: Uint8Array | undefined,
  compression: string | undefined,
): Uint8Array | undefined {
  if (!data) return undefined;
  const normalized = normalizeInnerCompression(compression);
  if (!normalized) return data;
  if (normalized === "zlib-deflate") return unzlibSync(data);
  return undefined;
}

type FrontDataVerticalCrop = {
  rowStart: number;
  rowEnd: number;
};

function cprDisplayBlackHuThreshold(currentVOI?: VOI) {
  const windowLevel = Number(currentVOI?.windowLevel);
  const windowWidth = Number(currentVOI?.windowWidth);
  if (!Number.isFinite(windowLevel) || !Number.isFinite(windowWidth) || windowWidth <= 0) {
    return undefined;
  }
  return windowLevel - windowWidth * 0.5;
}

const FRONT_DATA_VISUAL_BLACK_ROW_RATIO = 0.98;

function isFrontDataHuBlankRow({
  hu,
  width,
  row,
  threshold,
}: {
  hu: Int16Array;
  labelBytes?: Uint8Array;
  width: number;
  row: number;
  threshold: number;
}) {
  const rowOffset = row * width;
  let blackPixels = 0;
  for (let x = 0; x < width; x += 1) {
    if (hu[rowOffset + x] <= threshold) blackPixels += 1;
  }
  return blackPixels / width >= FRONT_DATA_VISUAL_BLACK_ROW_RATIO;
}

function resolveFrontDataDisplayCrop({
  huEntryBytes,
  labelBytes,
  width,
  height,
  dtype,
  compression,
  currentVOI,
}: {
  huEntryBytes?: Uint8Array;
  labelBytes?: Uint8Array;
  width: number;
  height: number;
  dtype?: string;
  compression?: string;
  currentVOI?: VOI;
}): FrontDataVerticalCrop {
  const threshold = cprDisplayBlackHuThreshold(currentVOI);
  const cleanDtype = (dtype ?? "int16").trim().toLowerCase();
  if (threshold === undefined || !huEntryBytes || !cleanDtype.includes("int16")) {
    return { rowStart: 0, rowEnd: height };
  }

  const decoded = decodeFrontDataInnerBytes(huEntryBytes, compression);
  if (!decoded || decoded.byteLength !== width * height * 2) {
    return { rowStart: 0, rowEnd: height };
  }

  const hu = new Int16Array(decoded.buffer, decoded.byteOffset, decoded.byteLength / 2);
  let rowStart = 0;
  while (
    rowStart < height - 1 &&
    isFrontDataHuBlankRow({ hu, labelBytes, width, row: rowStart, threshold })
  ) {
    rowStart += 1;
  }

  let rowEnd = height;
  while (
    rowEnd - 1 > rowStart &&
    isFrontDataHuBlankRow({ hu, labelBytes, width, row: rowEnd - 1, threshold })
  ) {
    rowEnd -= 1;
  }

  if (rowStart <= 0 && rowEnd >= height) {
    return { rowStart: 0, rowEnd: height };
  }
  return { rowStart, rowEnd };
}

function cropFrontDataLabelRows(
  labelBytes: Uint8Array | undefined,
  width: number,
  height: number,
  crop: FrontDataVerticalCrop,
) {
  if (!labelBytes || crop.rowStart <= 0 && crop.rowEnd >= height) return labelBytes;
  if (labelBytes.byteLength < width * height) return labelBytes;
  return labelBytes.slice(crop.rowStart * width, crop.rowEnd * width);
}

function cropFrontDataMappingRows(
  mapping: FrontDataMapping | undefined,
  crop: FrontDataVerticalCrop,
  width: number,
  sourceHeight: number,
): FrontDataMapping | undefined {
  if (!mapping || (crop.rowStart <= 0 && crop.rowEnd >= sourceHeight)) return mapping;
  const rowStart = Math.max(0, Math.min(sourceHeight, crop.rowStart));
  const rowEnd = Math.max(rowStart + 1, Math.min(sourceHeight, crop.rowEnd));
  const height = rowEnd - rowStart;
  const sliceRows = <T,>(values: T[] | undefined) =>
    Array.isArray(values) ? values.slice(rowStart, rowEnd) : values;

  return {
    ...mapping,
    width: toPositiveInt(mapping.width ?? width, width),
    height,
    rowToCenterlineIndex: sliceRows(mapping.rowToCenterlineIndex),
    rowToCenterlineX: sliceRows(mapping.rowToCenterlineX),
    rowToDistanceMm: sliceRows(mapping.rowToDistanceMm),
    path: Array.isArray(mapping.path)
      ? mapping.path
          .map((point) => {
            const y = toOptionalNumber(point.y);
            if (y === undefined) return point;
            return {
              ...point,
              y: y - rowStart,
            };
          })
          .filter((point) => {
            const y = toOptionalNumber(point.y);
            return y === undefined || (y >= -1 && y <= height);
          })
      : mapping.path,
  };
}

function computeLabelCenterXByRow({
  labelBytes,
  width,
  height,
}: {
  labelBytes?: Uint8Array;
  width: number;
  height: number;
}): Array<number | undefined> | undefined {
  if (!labelBytes || labelBytes.byteLength < width * height) return undefined;
  const centers = new Array<number | undefined>(height);

  for (let y = 0; y < height; y += 1) {
    const rowOffset = y * width;
    let lumenMin = Number.POSITIVE_INFINITY;
    let lumenMax = Number.NEGATIVE_INFINITY;
    let anyMin = Number.POSITIVE_INFINITY;
    let anyMax = Number.NEGATIVE_INFINITY;

    for (let x = 0; x < width; x += 1) {
      const value = labelBytes[rowOffset + x];
      if (value > 0) {
        if (x < anyMin) anyMin = x;
        if (x > anyMax) anyMax = x;
      }
      if (value === 255) {
        if (x < lumenMin) lumenMin = x;
        if (x > lumenMax) lumenMax = x;
      }
    }

    if (Number.isFinite(lumenMin) && Number.isFinite(lumenMax)) {
      centers[y] = (lumenMin + lumenMax) * 0.5;
    } else if (Number.isFinite(anyMin) && Number.isFinite(anyMax)) {
      centers[y] = (anyMin + anyMax) * 0.5;
    }
  }

  return centers;
}

function getNearestDefinedRowValue(
  values: Array<number | undefined> | undefined,
  row: number,
  maxSearchDistance = 64,
) {
  if (!values?.length) return undefined;
  const clampedRow = Math.max(0, Math.min(values.length - 1, Math.round(row)));
  const exact = values[clampedRow];
  if (exact !== undefined) return exact;
  for (let distance = 1; distance <= maxSearchDistance; distance += 1) {
    const prev = values[clampedRow - distance];
    if (prev !== undefined) return prev;
    const next = values[clampedRow + distance];
    if (next !== undefined) return next;
  }
  return undefined;
}

function frameByIndexFromFrameVectors(
  frameVectors?: FrontDataFrameVectors,
): Record<number, CrossSectionFrameRef> | undefined {
  const samples = frameVectors?.samples;
  if (!Array.isArray(samples) || !samples.length) return undefined;

  const frameByIndex: Record<number, CrossSectionFrameRef> = {};
  samples.forEach((sample, fallbackIndex) => {
    const index = Math.floor(toFiniteNumber(sample.centerlineIndex, fallbackIndex));
    const world = toVec3Tuple(sample.point);
    const tangent = normalizeTuple3(toVec3Tuple(sample.e0));
    const binormal = normalizeTuple3(toVec3Tuple(sample.e1));
    const normal = normalizeTuple3(
      toVec3Tuple(sample.tangent) ?? toVec3Tuple(sample.normal),
    );
    if (!world || !tangent || !binormal || !normal) return;
    frameByIndex[index] = {
      assetId: `front-data-frame-vector-${index}`,
      clientIndex: index,
      localIndex: index,
      sourceCenterlineIndex: index,
      world,
      normal,
      tangent,
      binormal,
    };
  });

  return Object.keys(frameByIndex).length ? frameByIndex : undefined;
}

function centerlinePathFromMapping({
  mapping,
  width,
  height,
  mode,
  labelCenterXByRow,
}: {
  mapping?: FrontDataMapping;
  width: number;
  height: number;
  mode?: CprMode;
  labelCenterXByRow?: Array<number | undefined>;
}): CprCenterlinePathPoint[] {
  const hasExplicitProjectedX =
    (Array.isArray(mapping?.path) &&
      mapping.path.some((point) => Number.isFinite(point?.x))) ||
    (Array.isArray(mapping?.rowToCenterlineX) &&
      mapping.rowToCenterlineX.some((value) => Number.isFinite(value)));
  const useLabelCenterX = mode !== "straightened" && !hasExplicitProjectedX;
  const fallbackCenterX = toFiniteNumber(mapping?.outputCenterX, width * 0.5);
  const projectedCenterXByRow = mapping?.rowToCenterlineX ?? [];
  const resolveProjectedX = (row: number) =>
    toOptionalNumber(projectedCenterXByRow[Math.round(row)]);
  const resolveX = (row: number, fallback: number) =>
    useLabelCenterX
      ? (getNearestDefinedRowValue(labelCenterXByRow, row) ?? fallback)
      : fallback;

  if (Array.isArray(mapping?.path) && mapping.path.length > 0) {
    return mapping.path.map((point, index) => {
      const y = toFiniteNumber(
        point.y,
        mapping.path!.length <= 1
          ? 0
          : (index / (mapping.path!.length - 1)) * Math.max(0, height - 1),
      );
      const projectedX = toOptionalNumber(point.x) ?? resolveProjectedX(y);
      const fallbackX = toFiniteNumber(projectedX, fallbackCenterX);
      return {
        index: Math.floor(toFiniteNumber(point.centerlineIndex, index)),
        x: resolveX(y, fallbackX),
        y,
        distanceMm: toOptionalNumber(point.distanceFromStartMm),
        sourceCenterlineIndex: toOptionalNumber(point.centerlineIndex),
      };
    });
  }

  const rowIndexes = mapping?.rowToCenterlineIndex ?? [];
  const rowDistances = mapping?.rowToDistanceMm ?? [];
  if (rowIndexes.length > 0) {
    return rowIndexes
      .map((centerlineIndex, row) => {
        if (centerlineIndex === null || centerlineIndex === undefined) {
          return null;
        }
        const fallbackX = toFiniteNumber(resolveProjectedX(row), fallbackCenterX);
        return {
          index: Math.floor(toFiniteNumber(centerlineIndex, row)),
          x: resolveX(row, fallbackX),
          y: row,
          distanceMm: toOptionalNumber(rowDistances[row]),
          sourceCenterlineIndex: toOptionalNumber(centerlineIndex),
        };
      })
      .filter((point): point is CprCenterlinePathPoint => !!point);
  }

  const count = Math.max(1, height);
  const centerX = width * 0.5;
  return Array.from({ length: count }, (_value, row) => ({
    index: row,
    x: resolveX(row, centerX),
    y: count <= 1 ? 0 : (row / (count - 1)) * Math.max(0, height - 1),
    sourceCenterlineIndex: row,
  }));
}

function inferFrontDataOutlineCoordinateScale(
  outline: FrontDataOutline | undefined,
  width: number,
  height: number,
) {
  const explicit = Number(outline?.coordinateScale);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  let maxX = 0;
  let maxY = 0;
  (outline?.lines ?? []).forEach((line) => {
    (line.points ?? []).forEach((point) => {
      const x = Number(point.x);
      const y = Number(point.y);
      if (Number.isFinite(x)) maxX = Math.max(maxX, Math.abs(x));
      if (Number.isFinite(y)) maxY = Math.max(maxY, Math.abs(y));
    });
  });

  if (maxX > width * 1.25 || maxY > height * 1.25) return 2;
  return 1;
}

function buildFrontDataOverlayGeometry({
  centerlinePath,
  outline,
  width,
  height,
  yOffset = 0,
  flipY = FRONT_DATA_OUTLINE_FLIP_Y,
}: {
  centerlinePath: CprCenterlinePathPoint[];
  outline?: FrontDataOutline;
  width: number;
  height: number;
  yOffset?: number;
  flipY?: boolean;
}): CprOverlayGeometry {
  const coordinateScale = inferFrontDataOutlineCoordinateScale(outline, width, height);
  const outlineLines: NonNullable<CprOverlayGeometry['outlineLines']> = (outline?.lines ?? [])
    .map((line, index) => {
      const points = (line.points ?? [])
        .map(finitePointOrNull)
        .filter((point): point is { x: number; y: number } => !!point)
        .map((point) => {
          const x = point.x / coordinateScale;
          const rawY = point.y / coordinateScale - yOffset;
          return {
            x,
            y: flipY ? flipYCoordinate(rawY, height) : rawY,
          };
        })
        .filter(
          (point) =>
            Number.isFinite(point.x) &&
            Number.isFinite(point.y) &&
            point.x >= -width &&
            point.x <= width * 2 &&
            point.y >= -height &&
            point.y <= height * 2,
        );

      if (points.length < 2) return null;
      return {
        key: `${line.type ?? 'outline'}-${index}`,
        type: line.type,
        closed: line.closed,
        points,
      };
    })
    .filter((line): line is NonNullable<CprOverlayGeometry['outlineLines']>[number] => !!line);

  return {
    origin: 'top-left',
    coordinateSpace: 'image',
    outlineLines,
  };
}

function buildFrameFromFrontData({
  zip,
  frame,
  mapping,
  outline,
  index,
  labelLayers,
  labelBytes,
  includePlaqueOutline,
  currentVOI,
}: {
  zip: LoadedFrontDataZip;
  frame: FrontDataFrame;
  mapping?: FrontDataMapping;
  outline?: FrontDataOutline;
  index: number;
  labelLayers?: ImageLayerLabelValues;
  labelBytes?: Uint8Array;
  includePlaqueOutline?: boolean;
  currentVOI?: VOI;
}): CprAngleFrame {
  const angleDeg = toFiniteNumber(frame.angleDeg ?? frame.angle, index * 5);
  const sourceWidth = toPositiveInt(frame.width ?? mapping?.width, 1);
  const sourceHeight = toPositiveInt(frame.height ?? mapping?.height, 1);
  const dtype = frame.huRawScalarType ?? "int16";
  const bytesPerPixel = dtypeBytes(dtype);
  const huPath = frame.huRawPath ?? frame.huPath ?? frame.basePath;
  if (!huPath) {
    throw new Error(
      `front_data CPR HU path missing: ${zip.zipPath} angle=${angleDeg}`,
    );
  }
  const labelPath = frame.labelPath;
  const huEntryBytes = extractOptionalZipEntryBytes(
    (zip as LoadedFrontDataZipWithBytes).zipBytes,
    zip.entries,
    huPath,
  );
  const crop = resolveFrontDataDisplayCrop({
    huEntryBytes,
    labelBytes,
    width: sourceWidth,
    height: sourceHeight,
    dtype,
    compression: frame.huRawCompression,
    currentVOI,
  });
  const cropHeight = Math.max(1, crop.rowEnd - crop.rowStart);
  const hasVerticalCrop = crop.rowStart > 0 || crop.rowEnd < sourceHeight;
  const cropTileRect = hasVerticalCrop
    ? {
        x: 0,
        y: crop.rowStart,
        width: sourceWidth,
        height: cropHeight,
      }
    : undefined;
  const width = sourceWidth;
  const height = cropHeight;
  const croppedMapping = cropFrontDataMappingRows(
    mapping,
    crop,
    sourceWidth,
    sourceHeight,
  );
  const croppedLabelBytes = cropFrontDataLabelRows(
    labelBytes,
    sourceWidth,
    sourceHeight,
    crop,
  );
  const huUncompressedBytes = sourceWidth * sourceHeight * bytesPerPixel;
  const labelUncompressedBytes = sourceWidth * sourceHeight;
  const base = createServerFrontDataCprBaseImagePath(zip.zipPath, huPath, {
    outputWidth: width,
    outputHeight: height,
    tileRect: cropTileRect,
    compression: normalizeInnerCompression(frame.huRawCompression),
    uncompressedBytes: huUncompressedBytes,
    dtype,
    huMin: undefined,
    huMax: undefined,
  });
  if (!base) {
    throw new Error(`front_data CPR base path create failed: ${zip.zipPath}`);
  }
  const labelCenterXByRow = computeLabelCenterXByRow({
    labelBytes: croppedLabelBytes,
    width,
    height,
  });
  const centerlinePath = centerlinePathFromMapping({
    mapping: croppedMapping,
    width,
    height,
    mode: zip.mode,
    labelCenterXByRow,
  });
  const overlayGeometry = includePlaqueOutline
    ? buildFrontDataOverlayGeometry({
        centerlinePath,
        outline,
        width,
        height,
        yOffset: crop.rowStart,
      })
    : undefined;

  return {
    angleDeg,
    width,
    height,
    labelPath,
    labelFlipY: false,
    labelUncompressedBytes,
    rowCenterlineIndex: croppedMapping?.rowToCenterlineIndex?.map((value) =>
      value === null || value === undefined ? -1 : value,
    ),
    rowDistanceMm: croppedMapping?.rowToDistanceMm,
    pathClientIndex: centerlinePath.map((point) => point.index),
    pathDistanceMm: centerlinePath
      .map((point) => point.distanceMm)
      .filter((value): value is number => value !== undefined),
    layers: {
      base,
      ...makeServerFrontDataLabelOverlayLayers(
        zip.zipPath,
        labelPath,
        {
          outputWidth: width,
          outputHeight: height,
          tileRect: cropTileRect,
          flipY: false,
          compression: undefined,
          uncompressedBytes: labelUncompressedBytes,
        },
        labelLayers,
      ),
    },
    centerlinePath,
    defaultViewBox: {
      x: 0,
      y: 0,
      width,
      height,
    },
    overlayGeometry,
  };
}

function buildCprResourceFromZip({
  zip,
  labelLayers,
  includePlaqueOutline,
  currentVOI,
}: {
  zip: LoadedFrontDataZipWithBytes;
  labelLayers?: ImageLayerLabelValues;
  includePlaqueOutline?: boolean;
  currentVOI?: VOI;
}): FrontDataCprResource {
  const frames = zip.manifest.frames ?? [];
  const angleFrames = frames.map((frame, index) => {
    const mapping = frame.mappingPath
      ? parseJsonZipEntry<FrontDataMapping>(
          zip.zipBytes,
          zip.entries,
          frame.mappingPath,
        )
      : undefined;
    const outline = includePlaqueOutline
      ? parseOutlineZipEntry(
          zip.zipBytes,
          zip.entries,
          frame.outlinePath,
          toFiniteNumber(frame.angleDeg ?? frame.angle, index * 5),
        )
      : undefined;
    const labelBytes = extractOptionalZipEntryBytes(
      zip.zipBytes,
      zip.entries,
      frame.labelPath,
    );
    return buildFrameFromFrontData({
      zip,
      frame,
      mapping,
      outline,
      index,
      labelLayers,
      labelBytes,
      includePlaqueOutline,
      currentVOI,
    });
  });

  return {
    mode: zip.mode,
    angleFrames,
  };
}

function buildCprResourceFromZipCached({
  zip,
  labelLayers,
  includePlaqueOutline,
  currentVOI,
}: {
  zip: LoadedFrontDataZipWithBytes;
  labelLayers?: ImageLayerLabelValues;
  includePlaqueOutline?: boolean;
  currentVOI?: VOI;
}): FrontDataCprResource {
  const cache = currentFrontDataSeriesCache;
  const cacheKey = [
    zip.zipPath,
    zip.mode,
    zip.manifestPathInZip,
    zip.manifest.frames?.length ?? 0,
    stableStringify(labelLayers ?? null),
    includePlaqueOutline ? "plaque-outline=1" : "plaque-outline=0",
    currentVOI?.windowLevel ?? "wl-default",
    currentVOI?.windowWidth ?? "ww-default",
  ].join("|");
  const cached = cache?.cprResourcesByCacheKey.get(cacheKey);
  if (cached) return cached;

  const resource = buildCprResourceFromZip({
    zip,
    labelLayers,
    includePlaqueOutline,
    currentVOI,
  });
  if (
    cache &&
    currentFrontDataSeriesCache === cache &&
    currentFrontDataSeriesCache.requestToken === cache.requestToken
  ) {
    cache.cprResourcesByCacheKey.set(cacheKey, resource);
  }
  return resource;
}

function createFrontDataBranchManifestPath(branchId: number | string) {
  return `${FRONT_DATA_BRANCH_MANIFEST_PREFIX}${branchId}`;
}

function getFrontDataBranchIds(viewData: FrontDataViewData) {
  return (viewData.viewerDataExport?.branches ?? [])
    .map((branch) => toSafeInt(branch.branchId ?? branch.branchIndex))
    .filter((branchId): branchId is number => branchId !== undefined);
}

function createFrontDataBranchImageResource({
  branch,
  branchId,
  cprEntries,
  loaded,
  labelLayers,
  frameByIndex,
}: {
  branch?: FrontDataBranch;
  branchId: number;
  cprEntries?: Array<readonly [CprMode, ReturnType<typeof buildCprResourceFromZip>]>;
  loaded: boolean;
  labelLayers: ImageLayerLabelValues;
  frameByIndex?: Record<number, CrossSectionFrameRef>;
}): CprCrossSectionImages["branches"][string] {
  const firstFrame = cprEntries?.[0]?.[1]?.angleFrames?.[0];
  const frameVectorIndexEnd = frameByIndex
    ? Math.max(...Object.keys(frameByIndex).map((key) => Number(key)))
    : 0;
  const indexEnd = Math.max(
    0,
    frameVectorIndexEnd,
    toPositiveInt(branch?.centerlinePointCount ?? firstFrame?.height, 1) - 1,
  );

  return {
    name: branch?.branchName,
    source: branch?.branchNames?.join(",") ?? branch?.branchName,
    manifestPath: loaded ? undefined : createFrontDataBranchManifestPath(branchId),
    loaded,
    labelLayers,
    cpr: Object.fromEntries(cprEntries ?? []),
    crossSection: {
      width: 1,
      height: 1,
      indexStart: 0,
      indexEnd,
      labelLayers,
      assetByIndex: {},
      frameByIndex: frameByIndex ?? {},
      assets: {},
    },
  };
}

async function loadFrontDataZipsForBranchIds({
  accessToken,
  seriesId,
  analysisType,
  branchIds,
  viewData,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  branchIds: readonly number[];
  viewData: FrontDataViewData;
}) {
  const selectedBranchIds = Array.from(new Set(branchIds));

  const loadTasks = selectedBranchIds.flatMap((branchId) => {
    const branch = findBranch(viewData, branchId);
    if (!branch) {
      console.warn("[front_data] branch not found in view_data", {
        analysisType,
        branchId,
      });
      return [];
    }

    return selectModes(viewData, branch).map((mode) =>
      loadFrontDataZipWithBytesCached({
        accessToken,
        seriesId,
        branch,
        branchId,
        mode,
      }),
    );
  });

  const loadedZips = await Promise.all(loadTasks);
  return { selectedBranchIds, loadedZips };
}

async function loadAortaFrontDataZip({
  accessToken,
  seriesId,
}: {
  accessToken: string;
  seriesId: string;
}): Promise<LoadedFrontDataZipWithBytes> {
  let lastError: unknown;

  for (const candidate of DEFAULT_AORTA_CPR_ZIP_PATHS) {
    try {
      return await loadFrontDataZipWithBytesCached({
        accessToken,
        seriesId,
        branch: createAortaFrontDataBranch(DEFAULT_AORTA_BRANCH_IDS[0]),
        branchId: DEFAULT_AORTA_BRANCH_IDS[0],
        mode: {
          mode: "straightened",
          zipPath: candidate,
          manifestPathInZip: "image_extraction_manifest.json",
          huDirectoryInZip: "hu",
          labelDirectoryInZip: "label",
          mappingDirectoryInZip: "mapping",
          outlineDirectoryInZip: "outline",
        },
      });
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError ?? new Error("front_data aorta CPR zip not found");
}

function createAortaFrontDataBranch(branchId: number): FrontDataBranch {
  return {
    branchId,
    branchIndex: branchId,
    branchName: "Aorta",
    branchNames: ["Aorta"],
  };
}

async function loadSelectedFrontDataZips({
  accessToken,
  seriesId,
  analysisType,
  mainBranchIds,
  viewData,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
  viewData: FrontDataViewData;
}) {
  if (isAorticFrontDataAnalysisType(analysisType)) {
    const loaded = await loadAortaFrontDataZip({ accessToken, seriesId });
    const selectedBranchIds = DEFAULT_AORTA_BRANCH_IDS;
    return {
      selectedBranchIds,
      loadedZips: selectedBranchIds.map((branchId) => ({
        ...loaded,
        branchId,
        branchName: "Aorta",
      })),
    };
  }

  const selectedBranchIds = selectBranchIdsForAnalysis({
    analysisType,
    mainBranchIds,
    viewData,
  });

  return loadFrontDataZipsForBranchIds({
    accessToken,
    seriesId,
    analysisType,
    branchIds: selectedBranchIds,
    viewData,
  });
}


export function isServerCprFrontDataAnalysisType(
  analysisType: AnalysisType | string | null | undefined,
): analysisType is AnalysisType {
  return (
    !!analysisType && SUPPORTED_ANALYSIS_TYPES.has(analysisType as AnalysisType)
  );
}

function frontDataImageManifestCacheKey({
  seriesId,
  analysisType,
  mainBranchIds,
  currentVOI,
}: {
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
  currentVOI?: VOI;
}) {
  return [
    seriesId,
    analysisType,
    ...(mainBranchIds ?? []),
    currentVOI?.windowLevel ?? "wl-default",
    currentVOI?.windowWidth ?? "ww-default",
  ].join("|");
}

export async function loadServerCprFrontDataImageManifest({
  accessToken,
  seriesId,
  analysisType,
  mainBranchIds,
  currentVOI,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
  currentVOI?: VOI;
}): Promise<CprCrossSectionImages> {
  if (!isServerCprFrontDataAnalysisType(analysisType)) {
    throw new Error(`unsupported front_data analysis type: ${analysisType}`);
  }

  const cache = getCurrentFrontDataSeriesCache(seriesId);
  const manifestCacheKey = frontDataImageManifestCacheKey({
    seriesId,
    analysisType,
    mainBranchIds,
    currentVOI,
  });
  const cachedManifest = cache.imageManifestsByCacheKey.get(manifestCacheKey);
  if (cachedManifest) {
    logFrontDataDebug("[front_data][cache] manifest hit", {
      seriesId,
      analysisType,
      cacheKey: manifestCacheKey,
    });
    return cachedManifest;
  }

  logFrontDataDebug("[front_data][cache] manifest miss", {
    seriesId,
    analysisType,
    cacheKey: manifestCacheKey,
  });

  const viewData = await loadViewData({ accessToken, seriesId });
  const { selectedBranchIds, loadedZips } = await loadSelectedFrontDataZips({
    accessToken,
    seriesId,
    analysisType,
    mainBranchIds,
    viewData,
  });

  logFrontDataDebug("[front_data] view_data loaded", {
    seriesId,
    analysisType,
    branchCount: viewData.viewerDataExport?.branchCount,
    modes: viewData.viewerDataExport?.modes,
    selectedBranchIds,
  });

  const initiallyLoadedBranchIds = new Set(selectedBranchIds.map(String));
  const isAorticFrontData = isAorticFrontDataAnalysisType(analysisType);
  const labelLayers = getFrontDataLabelLayersForAnalysisType(analysisType);
  const includePlaqueOutline = shouldIncludeFrontDataPlaqueOutline(analysisType);
  const branchIdsForManifest = isAorticFrontData
    ? selectedBranchIds
    : getFrontDataBranchIds(viewData);
  const branchEntries = branchIdsForManifest.map((branchId) => {
    const branch = isAorticFrontData
      ? createAortaFrontDataBranch(branchId)
      : findBranch(viewData, branchId);
    const zipsForBranch = loadedZips.filter((zip) => zip.branchId === branchId);
    const cprEntries = zipsForBranch.map(
      (zip) =>
        [
          zip.mode,
          buildCprResourceFromZipCached({
            zip,
            labelLayers,
            includePlaqueOutline,
            currentVOI,
          }),
        ] as const,
    );
    const frameByIndex = frameByIndexFromFrameVectors(
      zipsForBranch.find((zip) => zip.frameVectors?.samples?.length)
        ?.frameVectors,
    );
    const loaded = initiallyLoadedBranchIds.has(String(branchId));
    return [
      String(branchId),
      createFrontDataBranchImageResource({
        branch,
        branchId,
        cprEntries,
        loaded,
        labelLayers,
        frameByIndex,
      }),
    ];
  });
  if (isAorticFrontData && branchEntries.length > 0) {
    branchEntries.push(["aorta", branchEntries[0][1]]);
  }

  const manifest: CprCrossSectionImages = {
    revision: [
      "front-data",
      seriesId,
      analysisType,
      selectedBranchIds.join("-"),
      viewData.viewerDataExport?.branchCount ?? "unknown",
    ].join(":"),
    displayImage: {
      windowCenter: currentVOI?.windowLevel,
      windowWidth: currentVOI?.windowWidth,
    },
    branches: Object.fromEntries(branchEntries),
  };

  logFrontDataDebug("[front_data] CPR image manifest ready", {
    analysisType,
    branchIds: Object.keys(manifest.branches),
    loadedZipCount: loadedZips.length,
    loaded: loadedZips.map((zip) => ({
      branchId: zip.branchId,
      branchName: zip.branchName,
      mode: zip.mode,
      zipPath: zip.zipPath,
      entryCount: zip.entryCount,
      manifestFrameCount: zip.manifest.frames?.length,
      directoryCounts: zip.directoryCounts,
    })),
  });

  if (
    currentFrontDataSeriesCache === cache &&
    currentFrontDataSeriesCache.seriesId === seriesId &&
    currentFrontDataSeriesCache.requestToken === cache.requestToken
  ) {
    cache.imageManifestsByCacheKey.set(manifestCacheKey, manifest);
  }

  return manifest;
}

export function loadServerCprFrontDataImageManifestOnce({
  accessToken,
  seriesId,
  analysisType,
  mainBranchIds,
  currentVOI,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
  currentVOI?: VOI;
}) {
  const key = frontDataImageManifestCacheKey({
    seriesId,
    analysisType,
    mainBranchIds,
    currentVOI,
  });
  const existing = pendingImageManifests.get(key);
  if (existing) {
    logFrontDataDebug("[front_data][cache] manifest pending", {
      seriesId,
      analysisType,
      cacheKey: key,
    });
    return existing;
  }

  const promise = loadServerCprFrontDataImageManifest({
    accessToken,
    seriesId,
    analysisType,
    mainBranchIds,
    currentVOI,
  }).catch((err) => {
    pendingImageManifests.delete(key);
    throw err;
  });
  pendingImageManifests.set(key, promise);
  return promise;
}

export function isServerCprFrontDataImageManifest(
  manifest: CprCrossSectionImages | null | undefined,
) {
  return !!manifest?.revision?.startsWith("front-data:");
}

export function isServerCprFrontDataBranchDeferred(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
) {
  if (!isServerCprFrontDataImageManifest(manifest)) return false;
  if (branchId === null || branchId === undefined) return false;
  const branch = manifest?.branches[String(branchId)];
  return (
    !!branch?.manifestPath?.startsWith(FRONT_DATA_BRANCH_MANIFEST_PREFIX) &&
    branch.loaded !== true
  );
}

export async function loadServerCprFrontDataBranchResource({
  accessToken,
  seriesId,
  manifest,
  branchId,
  analysisType,
  currentVOI,
}: {
  accessToken: string;
  seriesId: string;
  manifest: CprCrossSectionImages;
  branchId: number | string;
  analysisType: AnalysisType;
  currentVOI?: VOI;
}): Promise<{
  branchId: string;
  branch: CprCrossSectionImages["branches"][string];
}> {
  const parsedBranchId = toSafeInt(branchId);
  if (parsedBranchId === undefined) {
    throw new Error(`invalid front_data branch id: ${branchId}`);
  }

  if (!isServerCprFrontDataBranchDeferred(manifest, parsedBranchId)) {
    const branch = manifest.branches[String(parsedBranchId)];
    if (!branch) throw new Error(`front_data branch missing: ${parsedBranchId}`);
    return { branchId: String(parsedBranchId), branch };
  }

  const cache = getCurrentFrontDataSeriesCache(seriesId);
  const branchResourceCacheKey = [
    manifest.revision ?? "no-revision",
    analysisType,
    parsedBranchId,
    currentVOI?.windowLevel ?? "wl-default",
    currentVOI?.windowWidth ?? "ww-default",
  ].join("|");
  const cachedBranchResource = cache.branchResourcesByCacheKey.get(
    branchResourceCacheKey,
  );
  if (cachedBranchResource) {
    logFrontDataDebug("[front_data][cache] branch resource hit", {
      seriesId,
      analysisType,
      branchId: parsedBranchId,
      cacheKey: branchResourceCacheKey,
    });
    return cachedBranchResource;
  }

  logFrontDataDebug("[front_data][cache] branch resource miss", {
    seriesId,
    analysisType,
    branchId: parsedBranchId,
    cacheKey: branchResourceCacheKey,
  });

  const viewData = await loadViewData({ accessToken, seriesId });
  const isAorticFrontData = isAorticFrontDataAnalysisType(analysisType);
  const branch = isAorticFrontData
    ? createAortaFrontDataBranch(parsedBranchId)
    : findBranch(viewData, parsedBranchId);
  if (!branch) {
    throw new Error(`front_data branch not found in view_data: ${parsedBranchId}`);
  }

  const { loadedZips } = isAorticFrontData
    ? await loadSelectedFrontDataZips({
        accessToken,
        seriesId,
        analysisType,
        mainBranchIds: [parsedBranchId],
        viewData,
      })
    : await loadFrontDataZipsForBranchIds({
        accessToken,
        seriesId,
        analysisType,
        branchIds: [parsedBranchId],
        viewData,
      });
  const zipsForBranch = loadedZips.filter((zip) => zip.branchId === parsedBranchId);
  const labelLayers = getFrontDataLabelLayersForAnalysisType(analysisType);
  const includePlaqueOutline = shouldIncludeFrontDataPlaqueOutline(analysisType);
  const cprEntries = zipsForBranch.map(
    (zip) =>
      [
        zip.mode,
        buildCprResourceFromZipCached({
          zip,
          labelLayers,
          includePlaqueOutline,
          currentVOI,
        }),
      ] as const,
  );

  const frameByIndex = frameByIndexFromFrameVectors(
    zipsForBranch.find((zip) => zip.frameVectors?.samples?.length)
      ?.frameVectors,
  );

  const result: FrontDataBranchResourceLoadResult = {
    branchId: String(parsedBranchId),
    branch: createFrontDataBranchImageResource({
      branch,
      branchId: parsedBranchId,
      cprEntries,
      loaded: true,
      labelLayers,
      frameByIndex,
    }),
  };
  if (
    currentFrontDataSeriesCache === cache &&
    currentFrontDataSeriesCache.seriesId === seriesId &&
    currentFrontDataSeriesCache.requestToken === cache.requestToken
  ) {
    cache.branchResourcesByCacheKey.set(branchResourceCacheKey, result);
  }
  return result;
}

export function loadServerCprFrontDataBranchResourceOnce({
  accessToken,
  seriesId,
  manifest,
  branchId,
  analysisType,
  currentVOI,
}: {
  accessToken: string;
  seriesId: string;
  manifest: CprCrossSectionImages;
  branchId: number | string;
  analysisType: AnalysisType;
  currentVOI?: VOI;
}) {
  const key = [
    seriesId,
    manifest.revision,
    branchId,
    analysisType,
    currentVOI?.windowLevel ?? "wl-default",
    currentVOI?.windowWidth ?? "ww-default",
  ].join("|");
  const existing = pendingBranchManifests.get(key);
  if (existing) return existing;

  const promise = loadServerCprFrontDataBranchResource({
    accessToken,
    seriesId,
    manifest,
    branchId,
    analysisType,
    currentVOI,
  }).catch((err) => {
    pendingBranchManifests.delete(key);
    throw err;
  });
  pendingBranchManifests.set(key, promise);
  return promise;
}

export async function loadServerCprFrontDataDebugBundle({
  accessToken,
  seriesId,
  analysisType,
  mainBranchIds,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
}) {
  if (!isServerCprFrontDataAnalysisType(analysisType)) return [];

  const viewData = await loadViewData({ accessToken, seriesId });
  if (isAorticFrontDataAnalysisType(analysisType)) {
    const { selectedBranchIds, loadedZips } = await loadSelectedFrontDataZips({
      accessToken,
      seriesId,
      analysisType,
      mainBranchIds,
      viewData,
    });
    const summaries = loadedZips.map((zip) => ({
      branchId: zip.branchId,
      branchName: zip.branchName,
      mode: zip.mode,
      zipPath: zip.zipPath,
      zipByteLength: zip.zipByteLength,
      entryCount: zip.entryCount,
      manifestPathInZip: zip.manifestPathInZip,
      manifestFrameCount: zip.manifest.frames?.length,
      firstFrame: zip.manifest.frames?.[0],
      directoryCounts: zip.directoryCounts,
    }));
    logFrontDataDebug("[front_data] CPR zip load summary", {
      analysisType,
      requestedBranchIds: selectedBranchIds,
      loadedZipCount: summaries.length,
      loaded: summaries,
    });
    return summaries;
  }

  const selectedBranchIds = selectBranchIdsForAnalysis({
    analysisType,
    mainBranchIds,
    viewData,
  });

  logFrontDataDebug("[front_data] view_data loaded", {
    seriesId,
    analysisType,
    branchCount: viewData.viewerDataExport?.branchCount,
    modes: viewData.viewerDataExport?.modes,
    selectedBranchIds,
  });

  const loadTasks = selectedBranchIds.flatMap((branchId) => {
    const branch = findBranch(viewData, branchId);
    if (!branch) {
      console.warn("[front_data] branch not found in view_data", {
        analysisType,
        branchId,
      });
      return [];
    }

    return selectModes(viewData, branch).map((mode) =>
      loadZipSummary({ accessToken, seriesId, branch, branchId, mode })
        .then((summary) => {
          logFrontDataDebug("[front_data] CPR zip loaded", summary);
          return summary;
        })
        .catch((err) => {
          console.warn("[front_data] CPR zip load failed", {
            analysisType,
            branchId,
            branchName: branch.branchName,
            mode: mode.mode,
            zipPath: mode.zipPath ?? mode.zipFileName,
            err,
          });
          return null;
        }),
    );
  });

  const summaries = (await Promise.all(loadTasks)).filter(
    (summary): summary is LoadedZipSummary => summary !== null,
  );

  logFrontDataDebug("[front_data] CPR zip load summary", {
    analysisType,
    requestedBranchIds: selectedBranchIds,
    loadedZipCount: summaries.length,
    loaded: summaries.map((summary) => ({
      branchId: summary.branchId,
      branchName: summary.branchName,
      mode: summary.mode,
      zipPath: summary.zipPath,
      entryCount: summary.entryCount,
      manifestFrameCount: summary.manifestFrameCount,
      directoryCounts: summary.directoryCounts,
    })),
  });

  return summaries;
}

export function loadServerCprFrontDataDebugBundleOnce({
  accessToken,
  seriesId,
  analysisType,
  mainBranchIds,
}: {
  accessToken: string;
  seriesId: string;
  analysisType: AnalysisType;
  mainBranchIds?: readonly number[];
}) {
  const key = [seriesId, analysisType, ...(mainBranchIds ?? [])].join("|");
  const existing = pendingDebugLoads.get(key);
  if (existing) return existing;

  const promise = loadServerCprFrontDataDebugBundle({
    accessToken,
    seriesId,
    analysisType,
    mainBranchIds,
  }).catch((err) => {
    pendingDebugLoads.delete(key);
    throw err;
  });
  pendingDebugLoads.set(key, promise);
  return promise;
}
